import { useState } from 'react';
import { AreaConfig, SubjectConfig, Subject3DConfig, LabelConfig } from '#server/models';
import { useAdminSubjectDraft } from '@/contexts';
import { buildSqlStatements } from '../lib/buildSqlStatements';

// ─── Local helpers ─────────────────────────────────────────────────────────────

const buildSubjectConfig = (draft: ReturnType<typeof useAdminSubjectDraft>['draft']): SubjectConfig => {
  const config3D: Subject3DConfig | undefined =
    draft.type === '3D'
      ? {
          modelPath: draft.config3D.modelPath,
          texturePath: draft.config3D.texturePath,
          contextModelPath: draft.config3D.contextModelPath,
          referenceContextModelPath: draft.config3D.referenceContextModelPath,
          scale: draft.config3D.scale,
          position: draft.config3D.position,
          rotation: draft.config3D.rotation,
          distMin: draft.config3D.distMin,
          distMax: draft.config3D.distMax,
          dist: draft.config3D.dist,
          ambientLight: draft.config3D.ambientLight,
          axes: draft.config3D.axes,
          axesLabels: draft.config3D.axesLabels,
          labelVisibleDist: draft.config3D.labelVisibleDist,
          labelVisibleRange: draft.config3D.labelVisibleRange,
          context: {
            ...(draft.config3D.context.subjectContextEnabled
              ? { subjectContext: draft.config3D.context.subjectContext }
              : {}),
            referenceContext: draft.config3D.context.referenceContext,
            fullContext: draft.config3D.context.fullContext
          }
        }
      : undefined;

  const labels: LabelConfig[] = draft.labels.map((l) => ({
    id: l.id ?? 0,
    slug: l.slug,
    title: l.title,
    description: l.description,
    type: draft.type,
    primaryCategoryId: l.primaryCategoryId ?? { id: 0, groupId: 0 },
    categoryIds: l.categoryIds,
    unitIds: l.unitIds,
    ...(draft.type === '3D' ? { config3D: l.config3D } : {})
  }));

  return {
    id: draft.id ?? 0,
    slug: draft.slug,
    title: draft.title,
    type: draft.type,
    discipline: draft.disciplineId ?? 0,
    description: draft.description,
    thumbnailPath: draft.thumbnailPath,
    categoryIds: draft.categoryIds,
    unitIds: draft.unitIds,
    config3D,
    labels
  };
};

const PANEL_BTN = 'px-3 py-1.5 text-sm rounded border border-[#555] text-[#CCC] hover:bg-[#333] transition-colors';

const ExportPanelHeader = ({
  title,
  copied,
  copyLabel,
  downloadLabel,
  onCopy,
  onDownload,
}: {
  title: string;
  copied: boolean;
  copyLabel: string;
  downloadLabel: string;
  onCopy: () => void;
  onDownload: () => void;
}) => (
  <div className="flex-1 min-w-0 flex items-center justify-between">
    <h2 className="text-base font-semibold text-[#E8E8E8]">{title}</h2>
    <div className="flex gap-2">
      <button onClick={onCopy} className={PANEL_BTN}>
        {copied ? '✓ Copied!' : copyLabel}
      </button>
      <button onClick={onDownload} className={PANEL_BTN}>
        {downloadLabel}
      </button>
    </div>
  </div>
);

// ─── Step ─────────────────────────────────────────────────────────────────────

interface Step6Props {
  areaConfig: AreaConfig;
}

export const Step6Export = ({ areaConfig }: Step6Props) => {
  const { draft, areaSlug, isNew, initialDraft } = useAdminSubjectDraft();
  const [copiedJson, setCopiedJson] = useState(false);
  const [copiedSql, setCopiedSql] = useState(false);

  const config = buildSubjectConfig(draft);
  const json = JSON.stringify(config, null, 2);
  const { sql, assetWarnings } = buildSqlStatements(draft, areaSlug, isNew, initialDraft);

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopiedJson(true);
      setTimeout(() => setCopiedJson(false), 2000);
    } catch {
      // fallback: no-op
    }
  };

  const handleCopySql = async () => {
    try {
      await navigator.clipboard.writeText(sql);
      setCopiedSql(true);
      setTimeout(() => setCopiedSql(false), 2000);
    } catch {
      // fallback: no-op
    }
  };

  const handleDownloadJson = () => {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${draft.slug || areaConfig.subjectName.singular.toLowerCase()}-config.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadSql = () => {
    const blob = new Blob([sql], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${draft.slug || areaConfig.subjectName.singular.toLowerCase()}-${isNew ? 'insert' : 'update'}.sql`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header row */}
      <div className="flex gap-6 mb-3 flex-shrink-0">
        <ExportPanelHeader
          title="JSON – Config"
          copied={copiedJson}
          copyLabel="Copy JSON"
          downloadLabel="Download JSON"
          onCopy={handleCopyJson}
          onDownload={handleDownloadJson}
        />
        <ExportPanelHeader
          title={isNew ? 'SQL – Insert' : 'SQL – Update (diff)'}
          copied={copiedSql}
          copyLabel="Copy SQL"
          downloadLabel="Download SQL"
          onCopy={handleCopySql}
          onDownload={handleDownloadSql}
        />
      </div>

      {/* Content row */}
      <div className="flex gap-6 flex-1 min-h-0">
        {/* Left: JSON panel */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          <pre className="flex-1 overflow-auto p-4 rounded bg-[#111] border border-[#333] text-xs text-[#CCC] font-mono scrollbar scrollbar-track-[#111] scrollbar-thumb-[#303030] scrollbar-thumb-rounded-full scrollbar-track-rounded-full">
            {json}
          </pre>
        </div>

        {/* Right: SQL panel */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          {assetWarnings.length > 0 && (
            <div className="mb-3 p-3 rounded border border-[#F59E0B] bg-[#F59E0B]/10 space-y-1.5 flex-shrink-0">
              <p className="text-xs font-semibold text-[#F59E0B] uppercase tracking-wide">
                S3 Upload Required
              </p>
              <p className="text-xs text-[#999]">
                Upload these assets to S3 before this configuration will work:
              </p>
              <ul className="space-y-1">
                {assetWarnings.map((w) => (
                  <li key={w.path} className="flex items-start gap-2 text-xs">
                    <span className={`shrink-0 font-mono font-semibold ${w.isAdded ? 'text-[#C3D82E]' : 'text-[#F59E0B]'}`}>
                      {w.isAdded ? '+ NEW' : '~ CHANGED'}
                    </span>
                    <span className="text-[#888]">{w.field}:</span>
                    <span className="text-[#CCC] break-all">{w.path}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <pre className="flex-1 overflow-auto p-4 rounded bg-[#111] border border-[#333] text-xs text-[#CCC] font-mono scrollbar scrollbar-track-[#111] scrollbar-thumb-[#303030] scrollbar-thumb-rounded-full scrollbar-track-rounded-full">
            {sql}
          </pre>
        </div>
      </div>
    </div>
  );
};
